import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { runJsSandboxed } from '../../lib/jsSandbox';

// A coding-question answer surface: a monospace editor, a fixed language label
// (the AI generator decides the language — the candidate can't switch it), and a
// "Run" button. Python runs on the sandboxed backend; JavaScript runs in an
// opaque-origin, network-less browser sandbox. Other languages are write & submit only.
const JS = ['javascript', 'js', 'node', 'typescript', 'ts'];
const BACKEND = ['python', 'python3', 'py', 'java', 'go', 'golang'];
const isJs = (l) => JS.includes((l || '').toLowerCase());
const isBackend = (l) => BACKEND.includes((l || '').toLowerCase());

export function CodeAnswer({ value, onChange, language, runPython }) {
  const lang = language || 'Python';
  const canRun = isBackend(lang) || isJs(lang);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const taRef = useRef(null);

  // Tab key inserts two spaces instead of leaving the editor.
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const el = e.target;
      const s = el.selectionStart, en = el.selectionEnd;
      const next = (value || '').slice(0, s) + '  ' + (value || '').slice(en);
      onChange(next);
      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
    }
  }, [value, onChange]);

  const handleRun = async () => {
    if (!canRun || running) return;
    setRunning(true);
    setOutput(null);
    try {
      const res = isJs(lang)
        ? await runJsSandboxed(value || '')
        : await runPython(value || '', lang);
      setOutput(res || { stdout: '', stderr: '' });
    } catch (e) {
      setOutput({ stdout: '', stderr: e?.response?.data?.detail || 'Could not run your code. Please try again.' });
    } finally {
      setRunning(false);
    }
  };

  const hasErr = output && (output.stderr && output.stderr.trim().length > 0);

  return (
    <div>
      {/* Editor header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.5rem 0.75rem', background: '#002A38', borderTopLeftRadius: 10, borderTopRightRadius: 10,
        border: '1px solid #002A38',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ display: 'flex', gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F87171' }} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FBBF24' }} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#34D399' }} />
          </span>
          <span data-testid="coding-language-label" style={{
            fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', color: '#E7E5E4', fontWeight: 600,
            padding: '0.15rem 0.55rem', borderRadius: 999, background: 'rgba(240, 80, 0,0.22)', border: '1px solid rgba(240, 80, 0,0.4)',
          }}>{lang}</span>
        </div>
        {canRun ? (
          <Button data-testid="code-run-button" onClick={handleRun} disabled={running} style={{
            height: 30, padding: '0 0.9rem', borderRadius: 8, background: running ? '#57534E' : '#0076A3', color: '#fff',
            fontSize: '0.75rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
            {running ? 'Running…' : 'Run'}
          </Button>
        ) : (
          <span style={{ fontSize: '0.68rem', color: '#A8A29E' }}>Run not available for {lang} — write & submit</span>
        )}
      </div>

      {/* Editor */}
      <textarea
        ref={taRef}
        data-testid="code-editor-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        placeholder={`Write your ${lang} solution here…`}
        rows={14}
        style={{
          width: '100%', padding: '1rem', border: '1px solid #002A38', borderTop: 'none',
          borderBottomLeftRadius: hasErr || output ? 0 : 10, borderBottomRightRadius: hasErr || output ? 0 : 10,
          background: '#0F0E0D', color: '#E7E5E4', fontSize: '0.82rem',
          fontFamily: 'JetBrains Mono, monospace', outline: 'none', resize: 'vertical',
          boxSizing: 'border-box', lineHeight: 1.7, tabSize: 2,
        }}
      />

      {/* Output console */}
      {output && (
        <div data-testid="code-output" style={{
          background: '#161513', border: '1px solid #002A38', borderTop: '1px solid #2A2522',
          borderBottomLeftRadius: 10, borderBottomRightRadius: 10, padding: '0.75rem 1rem',
          maxHeight: 220, overflow: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#A8A29E', fontWeight: 700 }}>Output</span>
            {output.timed_out && <span style={{ fontSize: '0.65rem', color: '#FBBF24' }}>timed out</span>}
            {typeof output.duration_ms === 'number' && !output.timed_out && (
              <span style={{ fontSize: '0.65rem', color: '#78716C' }}>{output.duration_ms} ms</span>
            )}
          </div>
          {output.stdout ? (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', color: '#D6FCE3' }}>{output.stdout}</pre>
          ) : null}
          {hasErr ? (
            <pre style={{ margin: output.stdout ? '8px 0 0' : 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.78rem', color: '#FCA5A5' }}>{output.stderr}</pre>
          ) : null}
          {!output.stdout && !hasErr ? (
            <div style={{ fontSize: '0.78rem', color: '#78716C', fontFamily: 'JetBrains Mono, monospace' }}>Program finished with no output.</div>
          ) : null}
        </div>
      )}

      <p style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', marginTop: 8 }}>
        {isJs(lang)
          ? 'Runs in a sandboxed in-browser JavaScript runner. Use console.log(...) to print output. Your code is saved as your answer.'
          : isBackend(lang)
            ? 'Runs in a sandboxed environment (no network, CPU / memory / time limits). Your code is saved as your answer.'
            : 'Your code is saved as your answer and reviewed by the hiring team.'}
      </p>
    </div>
  );
}
